import * as p from '@clack/prompts'
import type {
  RailwayDeployResult,
  RailwayService,
  RailwaySession
} from '@cli-types/next-adapter/railway.js'
import { DEPLOY_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { outputTail } from '@cli-utils/process.js'
import { spinner } from '@cli-utils/terminal.js'
import { guardProjectForDeploy } from '@next-adapter/init/deploy/guard/guard-project-for-deploy.js'
import { linkRailwayService } from '@next-adapter/init/railway/project/link-railway-service.js'
import { ensureRailwayAppService } from '@next-adapter/init/railway/resources/ensure-railway-app-service.js'
import { runRailway } from '@next-adapter/init/railway/runner/run-railway.js'
import pc from 'picocolors'
import { deploymentLine } from './deployment-line.js'
import { ensureRailwayDomain } from './ensure-railway-domain.js'
import { readFailedDeploymentLogs } from './read-failed-deployment-logs.js'
import { syncRailwayServiceEnv } from './sync-railway-service-env.js'

export async function runRailwayDeployFlow(options: {
  session: RailwaySession
  cwd: string
  service?: string
  providerOverrides?: Record<string, string>
}): Promise<RailwayDeployResult> {
  try {
    const prepSpinner = spinner()
    prepSpinner.start('Connecting the Railway web service')
    let service: RailwayService
    let url: string | undefined
    try {
      service = await ensureRailwayAppService(
        options.session,
        options.cwd,
        options.service?.trim() || 'web'
      )
      if ((service.replicaCount ?? 1) > 1) {
        throw new Error(
          `Railway service ${service.name} has ${service.replicaCount} replicas. BetterStart Railway v1 supports one replica because Next.js cache invalidation is process-local.`
        )
      }
      await linkRailwayService(
        options.session.runner,
        options.cwd,
        options.session.project,
        service,
        options.session.env
      )

      prepSpinner.message('Setting up the Railway domain')
      url = await ensureRailwayDomain(options.session, options.cwd, service)
    } finally {
      prepSpinner.clear()
    }
    if (!url) {
      throw new Error('Railway could not create a public domain required for Better Auth.')
    }
    const providerOverrides = {
      ...options.providerOverrides,
      BETTERSTART_AUTH_URL: url,
      NEXT_PUBLIC_BETTERSTART_AUTH_URL: url
    }

    const envSpinner = spinner()
    envSpinner.start('Syncing environment variables to Railway')
    const sync = await syncRailwayServiceEnv(
      options.session,
      options.cwd,
      service,
      providerOverrides
    )
    envSpinner.clear()
    for (const key of sync.failed) {
      p.log.warn(`Could not set ${pc.cyan(key)} on Railway.`)
    }
    if (sync.failed.length > 0) {
      return {
        ok: false,
        service,
        syncedEnvKeys: sync.synced,
        detail: `Railway environment sync failed for: ${sync.failed.join(', ')}.`
      }
    }

    const guardSpinner = spinner()
    guardSpinner.start('Preparing the project for deploy')
    const packageGuard = await guardProjectForDeploy(options.cwd)
    guardSpinner.clear()
    if (packageGuard.lockfile === 'failed') {
      p.log.warn(
        'Could not refresh pnpm-lock.yaml after removing betterstart-cli — the remote install may fail.'
      )
    }
    for (const dependency of packageGuard.localSpecDeps) {
      p.log.warn(
        `Dependency ${pc.cyan(dependency)} uses a local spec that cannot install on Railway.`
      )
    }

    const deploySpinner = spinner()
    deploySpinner.start('Deploying to Railway')
    let deploy: Awaited<ReturnType<typeof runRailway>>
    try {
      deploy = await runRailway(
        options.session.runner,
        [
          'up',
          '.',
          '--path-as-root',
          '--service',
          service.name,
          '--environment',
          options.session.project.environmentName,
          '--json'
        ],
        {
          cwd: options.cwd,
          mode: 'capture',
          timeoutMs: DEPLOY_TIMEOUT_MS,
          env: options.session.env,
          onOutputLine: (line) => {
            const message = deploymentLine(line)
            if (message) deploySpinner.message(message)
          }
        }
      )
    } finally {
      packageGuard.restore()
    }

    if (!deploy.success) {
      deploySpinner.stop(`${pc.yellow('▲')} Railway deploy did not complete`)
      const logs = await readFailedDeploymentLogs(options.session, options.cwd, service)
      return {
        ok: false,
        service,
        syncedEnvKeys: sync.synced,
        detail: logs ?? outputTail(`${deploy.stdout}\n${deploy.stderr}`) ?? deploy.errorMessage
      }
    }

    deploySpinner.stop(url ? `Deployed ${pc.cyan(url)}` : 'Deployed to Railway')
    return { ok: true, url, service, syncedEnvKeys: sync.synced }
  } catch (error) {
    return { ok: false, detail: error instanceof Error ? error.message : String(error) }
  }
}
